import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, PaintBrush, UploadSimple, Image as ImageIcon } from "@phosphor-icons/react";
import { TestEmailPanel } from "@/components/connect/TestEmailPanel";

const FIELDS = [
  ["business_name", "Business Name", "TinyLoan"],
  ["website", "Website", "tinyloan.in"],
  ["support_email", "Support Email", "info@tinyloan.in"],
  ["business_address", "Business Address (optional)", "2nd Floor, ..."],
];

export default function EmailBranding() {
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/connect/email/branding");
      setB(data.branding || {});
    } catch (e) { toast.error("Could not load branding"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setB((p) => ({ ...p, [k]: v }));

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be 2 MB or smaller."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/connect/email/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setB((p) => ({ ...p, logo: data.logo }));
      toast.success("Logo uploaded — it's now hosted and will render in emails.");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        business_name: b.business_name, website: b.website, support_email: b.support_email,
        logo: b.logo, business_address: b.business_address, footer_text: b.footer_text,
        signature: b.signature, disclaimer: b.disclaimer, accent_color: b.accent_color,
      };
      const { data } = await api.put("/connect/email/branding", payload);
      setB(data.branding);
      toast.success("Branding saved — it now wraps all your templates.");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!b) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  const accent = b.accent_color || "#0B3D91";

  return (
    <div data-testid="email-branding-page">
      <button onClick={() => nav("/connect")} className="text-xs text-muted-foreground flex items-center gap-1 mb-3 hover:text-foreground" data-testid="branding-back">
        <ArrowLeft size={14} weight="bold" /> Back to Connect
      </button>
      <div className="flex items-center gap-2 mb-1">
        <PaintBrush size={22} weight="bold" style={{ color: accent }} />
        <h1 className="text-2xl font-bold tracking-tight">Email Branding & Format</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-5">Set your email appearance once. It automatically wraps every template — change your logo or footer here and all emails update.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3 border border-border rounded-sm p-4 bg-card">
          {FIELDS.map(([k, label, ph]) => (
            <div key={k}>
              <Label className="text-xs">{label}</Label>
              <Input value={b[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} className="rounded-sm mt-1" data-testid={`branding-${k}`} />
            </div>
          ))}
          <div>
            <Label className="text-xs">Logo</Label>
            <div className="mt-1 flex items-center gap-3">
              <div className="h-14 w-14 rounded-sm border border-border bg-secondary/40 flex items-center justify-center overflow-hidden shrink-0" data-testid="branding-logo-thumb">
                {b.logo ? <img src={b.logo} alt="logo" className="max-h-14 max-w-14 object-contain" /> : <ImageIcon size={20} className="text-muted-foreground" />}
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" onChange={uploadLogo} className="hidden" data-testid="branding-logo-file" />
                <Button type="button" variant="outline" size="sm" className="rounded-sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="branding-logo-upload">
                  <UploadSimple size={14} weight="bold" className="mr-1.5" /> {uploading ? "Uploading…" : (b.logo ? "Replace Logo" : "Upload Logo")}
                </Button>
                <p className="text-[11px] text-muted-foreground mt-1">PNG, JPG, WebP or SVG · max 2 MB. Hosted at a URL so it renders in inboxes.</p>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Brand Accent Color</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={accent} onChange={(e) => set("accent_color", e.target.value)} className="h-9 w-12 rounded-sm border border-border" data-testid="branding-accent" />
              <Input value={accent} onChange={(e) => set("accent_color", e.target.value)} className="rounded-sm w-32 mono" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Default Footer Text</Label>
            <Textarea value={b.footer_text || ""} onChange={(e) => set("footer_text", e.target.value)} rows={2} className="rounded-sm mt-1" data-testid="branding-footer_text" />
          </div>
          <div>
            <Label className="text-xs">Default Signature</Label>
            <Textarea value={b.signature || ""} onChange={(e) => set("signature", e.target.value)} rows={2} className="rounded-sm mt-1" placeholder="Regards, The TinyLoan Team" data-testid="branding-signature" />
          </div>
          <div>
            <Label className="text-xs">Default Disclaimer</Label>
            <Textarea value={b.disclaimer || ""} onChange={(e) => set("disclaimer", e.target.value)} rows={2} className="rounded-sm mt-1" data-testid="branding-disclaimer" />
          </div>
          <Button onClick={save} disabled={saving} className="rounded-sm w-full" data-testid="branding-save">
            <FloppyDisk size={15} weight="bold" className="mr-1.5" /> {saving ? "Saving…" : "Save Branding"}
          </Button>
        </div>

        {/* Live preview */}
        <div>
          <div className="label-caps mb-2">Live Preview</div>
          <div className="border border-border rounded-sm overflow-hidden" data-testid="branding-preview">
            <div style={{ background: accent }} className="px-5 py-4 text-white">
              {b.logo ? <img src={b.logo} alt={b.business_name} style={{ maxHeight: 36 }} /> : <span className="font-bold text-lg">{b.business_name || "Your Business"}</span>}
              {b.website && <div className="text-xs mt-1 opacity-90">{b.website}</div>}
            </div>
            <div className="p-5 text-sm text-slate-800 leading-relaxed">
              <p>Dear Rahul Sharma,</p>
              <p className="mt-2">This is how your emails will look. Your template content appears here, wrapped in your branding.</p>
              {b.signature && <div className="mt-3 text-slate-700" dangerouslySetInnerHTML={{ __html: b.signature }} />}
            </div>
            <div className="px-5 py-3 bg-slate-50 border-t border-border text-[11px] text-slate-500 leading-relaxed">
              {b.footer_text}{b.business_address ? <><br />{b.business_address}</> : null}
              {b.support_email ? <><br />Support: {b.support_email}</> : null}
              {b.disclaimer ? <div className="mt-2">{b.disclaimer}</div> : null}
            </div>
          </div>
          <div className="mt-5"><TestEmailPanel /></div>
        </div>
      </div>
    </div>
  );
}
